import { useTranslation } from 'react-i18next';
import {

    HStack,
    Text,
    Kbd,
    Table,
    Tbody,
    Tr,
    Td,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    useDisclosure,
    IconButton,
    Tooltip,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { FiCommand } from 'react-icons/fi';
import { AppDialog } from '../common/AppDialog';
import { useCommandManager } from '../../state/commandStore';

interface CommandRow {
    label: string;
    description: string;
    shortcut: string;
}

const CATEGORY_ORDER = [
    'Architecture',
    'Structure',
    'Systems',
    'Modify',
    'Annotate',
    'View',
    'Massing & Site',
    'Analyze',
    'Collaborate',
    'Manage',
    'Insert',
    'Selection',
    'Properties',
    'Wall Tool',
    'Drafting',
    '3D Modeling',
    'Sketch',
    'File',
    'Edit',
    'Settings',
];

/**
 * Keyboard Shortcuts Help Panel
 *
 * Displays every registered command (live from the command store, so
 * user-assigned shortcuts from the Shortcut Editor are reflected) grouped by
 * its category tab.
 */
export function KeyboardShortcutsHelp() {
    const { t } = useTranslation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const commands = useCommandManager((s) => s.commands);

    const grouped = useMemo(() => {
        const groups = new Map<string, CommandRow[]>();
        Object.values(commands).forEach(cmd => {
            const category = CATEGORY_ORDER.includes(cmd.category) ? cmd.category : 'Other';
            const list = groups.get(category) ?? [];
            list.push({
                label: cmd.label,
                description: cmd.description ?? '',
                shortcut: cmd.shortcut ?? cmd.defaultShortcut ?? '',
            });
            groups.set(category, list);
        });
        const ordered: Array<[string, CommandRow[]]> = [];
        CATEGORY_ORDER.forEach(cat => {
            const rows = groups.get(cat);
            if (rows && rows.length > 0) ordered.push([cat, rows]);
        });
        groups.forEach((rows, cat) => {
            if (!CATEGORY_ORDER.includes(cat)) ordered.push([cat, rows]);
        });
        return ordered;
    }, [commands]);

    return (
        <>
            <Tooltip label={t('help.keyboardShortcuts')}>
                <IconButton
                    icon={<FiCommand />}
                    variant="ghost"
                    size="sm"
                    aria-label={t('help.keyboardShortcuts')}
                    onClick={onOpen}
                />
            </Tooltip>

            <AppDialog isOpen={isOpen} onClose={onClose} size="lg" icon={<FiCommand />} title={t('help.keyboardShortcuts')}>
                        {grouped.length === 0 ? (
                            <Text fontSize="sm" color="text.secondary">{t('help.noCommandsRegisteredYet')}</Text>
                        ) : (
                            <Tabs variant="soft-rounded" colorScheme="brand" size="sm" isLazy lazyBehavior="keepMounted">
                                <TabList flexWrap="wrap" gap={1}>
                                    {grouped.map(([category]) => (
                                        <Tab key={category}>
                                            {t(`help.categories.${category}`, { defaultValue: category })}
                                        </Tab>
                                    ))}
                                </TabList>

                                <TabPanels>
                                    {grouped.map(([category, rows]) => (
                                        <TabPanel key={category} px={0}>
                                            <Table size="sm" variant="simple">
                                                <Tbody>
                                                    {rows.map((row, i) => (
                                                        <ShortcutRow key={i} row={row} />
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </TabPanel>
                                    ))}
                                </TabPanels>
                            </Tabs>
                        )}
            </AppDialog>
        </>
    );
}

/**
 * Shortcut Row Component
 */
function ShortcutRow({ row }: { row: CommandRow }) {
    const keyParts = row.shortcut ? row.shortcut.split('+') : [];

    return (
        <Tr>
            <Td w="60%">
                <Text fontSize="sm">{row.label}</Text>
                {row.description && (
                    <Text fontSize="xs" color="text.secondary" noOfLines={1}>{row.description}</Text>
                )}
            </Td>
            <Td>
                {keyParts.length > 0 ? (
                    <HStack spacing={1}>
                        {keyParts.map((part, i) => (
                            <Kbd key={i} fontSize="xs">{part}</Kbd>
                        ))}
                    </HStack>
                ) : (
                    <Text fontSize="xs" color="text.secondary">—</Text>
                )}
            </Td>
        </Tr>
    );
}
